import React, { useEffect, useMemo, useState } from 'react'

import { AddNewButton } from '../components/AddNewButton'
import { DealTeamContacts } from './DealTeamContacts'
import {
  setAddContactDrawerOpen,
  setContactsCardsStatus,
  setFileContactsSubTab,
} from '@/client/generalSlice'
import { useAppDispatch, useAppSelector } from '@/client/hooks'
import FilterBadge from '@/components/FilterBadge'
import { ProfileTypes } from '@/constants/applicationProfile'
import { ApplicantCard } from '@/features/FileContacts/ApplicantCard'
import { ContactCard } from '@/features/FileContacts/ContactCard'
import { DeleteContactModal } from '@/features/FileContacts/DeleteContactModal'
import { NonApplicantCard } from '@/features/FileContacts/NonApplicantCard'
import parties from '@/features/FileContacts/parties'
import { useIsLockedOffer } from '@/graphql/services/application'
import { useGetDealTeamsByApplicationQuery } from '@/services/application'
import { useLazyGetUserDataQuery } from '@/services/userData'
import { useListApplicationPartiesQuery } from '@/services/v2-application/applicationParties'
import {
  useListApplicationProfilesQuery,
  useListNonApplicantsQuery,
} from '@/services/v2-application/applicationProfile'
import { User } from '@/types/unlock'

export const FileContacts = ({ application }) => {
  const dispatch = useAppDispatch()
  const { isLockedOffer } = useIsLockedOffer()
  const activeTab = useAppSelector((state) => state.general.fileContactsSubTab)
  const [showingDeleteModal, setShowingDeleteModal] = useState<boolean>(false)
  const [toBeDeletedFileContact, setToBeDeletedFileContact] = useState()
  const [salesContactEmail, setSalesContactEmail] = useState<string>('')
  const { data: appProfiles } = useListApplicationProfilesQuery(application?.uuid, {
    skip: !application?.uuid,
  })
  const { data: nonApplicants } = useListNonApplicantsQuery(application?.uuid, {
    skip: !application?.uuid,
  })
  const { data: applicationParties } = useListApplicationPartiesQuery(application?.uuid, {
    skip: !application?.uuid,
  })
  const [getUserById] = useLazyGetUserDataQuery()
  const { data: dealTeams } = useGetDealTeamsByApplicationQuery(application?.id, {
    skip: !application?.id,
  })

  const fetchSalesContactEmail = async (): Promise<string> => {
    if (dealTeams?.results.length > 0) {
      const salesContact = dealTeams.results.find((team) => team?.type === 'sales')
      const salesContactUser: User = await getUserById(salesContact?.user, true).unwrap()
      return salesContactUser.email
    }
    return ''
  }

  useEffect(() => {
    return () => {
      dispatch(setContactsCardsStatus('custom'))
    }
  }, [])

  useEffect(() => {
    fetchSalesContactEmail().then(setSalesContactEmail)
  }, [dealTeams])

  const buttons = {
    all: 'All',
    applicants: 'Applicants',
    'deal-team': 'Deal Team',
    'third-party': '3rd Parties',
  }

  const handleDeleteContact = (uuid) => {
    if (uuid) {
      setToBeDeletedFileContact(uuid)
      setShowingDeleteModal(true)
    }
  }

  const handleSendEmailWithCCToSales = (recipient?: string) => {
    window.location.href = `mailto:${recipient ?? ''}?cc=${salesContactEmail ?? ''}`
  }

  const dealTeamTypes = ['sales', 'processing', 'closing', 'post-closing', 'valuations', 'uam']

  const contactCards = useMemo(() => {
    let allParties = []

    if (activeTab == 'third-party') {
      allParties = parties.filter((party) => party.category == 'company')
    }

    if (allParties.length == 0) {
      allParties = parties
    }
    const appParties =
      allParties
        // Skip sales and processing parties, use DealTeamContacts instead
        ?.filter((party) => !dealTeamTypes.includes(party.type))
        ?.map((party, index) => {
          const applicationParty = applicationParties?.results?.find(
            (applicationParty) => applicationParty?.application_party_type === party.type
          )

          if (!applicationParty && !party.predefined) return null

          return (
            <ContactCard
              key={applicationParty?.uuid || index}
              index={applicationParty?.uuid || index}
              applicationParty={applicationParty}
              application={application}
              type={party}
              handleDeleteContact={handleDeleteContact}
              handleSendEmail={handleSendEmailWithCCToSales}
              shouldBeLocked={isLockedOffer}
            />
          )
        }) ?? []

    return appParties.filter((it) => !!it)
  }, [application, applicationParties, activeTab])

  const applicationProfiles = useMemo(() => {
    return appProfiles
      ? [...appProfiles.results].sort((profile1, profile2) => profile1.position - profile2.position)
      : []
  }, [appProfiles, salesContactEmail])

  const coOwners = useMemo(() => {
    return (
      nonApplicants?.results?.filter(
        (applicant) => applicant.profile_type === ProfileTypes.CO_OWNER
      ) || []
    )
  }, [nonApplicants])

  const nonOwningOccupants = useMemo(() => {
    return (
      nonApplicants?.results?.filter((applicant) => applicant.profile_type === ProfileTypes.NOO) ||
      []
    )
  }, [nonApplicants])

  const applicantCards = useMemo(() => {
    const applicants = applicationProfiles.map((appProfile) => {
      const { position } = appProfile
      const applicantTitle = `${position > 1 ? 'Co-' : ''}Applicant ${position > 1 ? position : ''}`

      return (
        <ApplicantCard
          key={appProfile.id}
          title={applicantTitle}
          applicationProfile={appProfile}
          application={application}
          handleSendEmail={handleSendEmailWithCCToSales}
          isCoApplicant={position > 1}
        />
      )
    })

    const coOwnersCards = coOwners?.map((appProfile) => {
      return (
        <NonApplicantCard
          key={appProfile.id}
          applicationProfile={appProfile}
          application={application}
          shouldBeLocked={isLockedOffer}
          handleSendEmail={handleSendEmailWithCCToSales}
          type={appProfile.profile_type}
        />
      )
    })

    const nonOwningOccupantsCards = nonOwningOccupants?.map((appProfile) => {
      return (
        <NonApplicantCard
          key={appProfile.id}
          applicationProfile={appProfile}
          application={application}
          shouldBeLocked={isLockedOffer}
          handleSendEmail={handleSendEmailWithCCToSales}
          type={appProfile.profile_type}
        />
      )
    })

    return applicants.concat(coOwnersCards).concat(nonOwningOccupantsCards)
  }, [applicationProfiles, coOwners, nonOwningOccupants])

  return (
    <>
      {showingDeleteModal && (
        <DeleteContactModal
          onClose={() => setShowingDeleteModal(false)}
          toBeDeletedFileContact={toBeDeletedFileContact}
        />
      )}
      <div className='w-full mb-2 flex justify-end px-2'>
        <AddNewButton onClick={() => dispatch(setAddContactDrawerOpen(true))}>Add New</AddNewButton>
      </div>
      <div className='border-t border-sky-200'>
        <div className='w-full p-2 mt-1'>
          <div className='flex gap-2 text-xs items-center'>
            {Object.keys(buttons).map(
              (button: 'all' | 'applicants' | 'deal-team' | 'third-party', key) => (
                <FilterBadge
                  key={key}
                  filterKey={button}
                  handleSelect={() => {
                    dispatch(setFileContactsSubTab(button))
                  }}
                  activeTab={activeTab}
                  label={buttons[button]}
                />
              )
            )}
          </div>
          <div className='mt-4 w-full text-xs flex flex-row gap-2 justify-end pr-2'>
            <p
              className='hover:underline cursor-pointer'
              onClick={() => dispatch(setContactsCardsStatus('allExpanded'))}
            >
              Expand All
            </p>
            <p>|</p>
            <p
              className='hover:underline cursor-pointer'
              onClick={() => dispatch(setContactsCardsStatus('allCollapsed'))}
            >
              Collapse All
            </p>
          </div>
        </div>
        <div className='flex flex-col gap-2 p-3 pt-1'>
          {(activeTab == 'all' || activeTab == 'applicants') && applicantCards}
          {(activeTab == 'all' || activeTab == 'deal-team') && (
            <DealTeamContacts
              application={application}
              shouldBeLocked={isLockedOffer}
            />
          )}
          {(activeTab == 'all' || activeTab == 'third-party') && contactCards}
        </div>
      </div>
    </>
  )
}
